'use client'

import { useMedia } from '@/hooks'
import { useStoreGeo, useStoreI18n } from '@/store'
import {
  Typo3SectionSpotSanityAccomodation,
  Typo3SectionSpotSanityCulture,
  Typo3SectionSpotSanityEatDrink,
  Typo3SectionSpotSanityEventList,
  Typo3SectionSpotSanityFreetime,
  Typo3SectionSpotSanityNature,
  Typo3SectionSpotSanityPressroom,
  Typo3SectionSpotSanityService,
  Typo3SectionSpotSanityShopping,
  Typo3SectionSpotSanitySport,
  Typo3SectionSpotSanitySportServices,
  Typo3SectionSpotSanityStory,
  Typo3SectionSpotSanityTour
} from '@/types'
import {
  Button,
  Dialog,
  InputSearch,
  SpotSanityFiltersModal,
  SpotSanitySorts
} from '@/ui/components'

const SpotSanityDates = dynamic(
  () => import('./FiltersComponents/SpotSanityDates'),
  { ssr: false }
)

import { cn } from '@/lib/utils'
import { hasActiveFacet } from '@/utils'
import { MEDIA } from '@spot'
import dynamic from 'next/dynamic'
import { memo, Suspense, useEffect } from 'react'
import { useGeolocated } from 'react-geolocated'

const Pdf = dynamic(() => import('./SpotSanityPdf'), { ssr: false })

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  data:
    | Typo3SectionSpotSanityAccomodation
    | Typo3SectionSpotSanityCulture
    | Typo3SectionSpotSanityService
    | Typo3SectionSpotSanityEatDrink
    | Typo3SectionSpotSanityFreetime
    | Typo3SectionSpotSanityNature
    | Typo3SectionSpotSanityShopping
    | Typo3SectionSpotSanitySport
    | Typo3SectionSpotSanitySportServices
    | Typo3SectionSpotSanityEventList
    | Typo3SectionSpotSanityTour
    | Typo3SectionSpotSanityStory
    | Typo3SectionSpotSanityPressroom
  store: any
  filterUrlForBackend?: string
}

const SpotSanityFilters = ({
  data,
  store,
  filterUrlForBackend,
  ...props
}: Props) => {
  const getVariant = (): 'hotel' | 'event' | 'tour' | 'story' | 'default' => {
    if (data.type === 'spotsanity_accommodation') {
      return 'hotel'
    } else if (data.type === 'spotsanity_eventlist') {
      return 'event'
    } else if (data.type === 'spotsanity_tour') {
      return 'tour'
    } else if (data.type === 'spotsanity_story') {
      return 'story'
    } else {
      return 'default'
    }
  }

  //stores
  const D = useStoreI18n.use.dictionary()
  const {
    storeItemsToShow,
    storeFilteredItems,
    facets,
    startDate,
    endDate,
    isOpenToday,
    setSearch,
    search,
    reset
  } = store()
  const setLocation = useStoreGeo.use.setLocation()
  const { coords, isGeolocationAvailable } = useGeolocated({
    positionOptions: {
      enableHighAccuracy: false
    },
    userDecisionTimeout: 5000
  })

  //hooks
  const isDesktop = useMedia(MEDIA.desktop, true)

  //Save GeoPosition
  useEffect(() => {
    if (isGeolocationAvailable && coords) {
      setLocation({
        latitude: coords.latitude,
        longitude: coords.longitude
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coords, isGeolocationAvailable])

  const isFiltersActive =
    facets.length > 0 || startDate || endDate || isOpenToday || search !== ''

  const filtersLength = facets?.length + (isOpenToday ? 1 : 0) || 0

  return (
    <>
      <div
        className="c-spot-sanity-filters gap-xs top-14 z-20 flex w-full justify-self-start md:top-16"
        {...props}
      >
        <InputSearch
          id="search"
          name="search"
          value={search}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setSearch((e.target as HTMLInputElement).value)
          }
          label={D['fe.events.filters.search' as any]}
          placeholder={D['fe.events.filters.keywords' as any]}
        />

        {isDesktop &&
          ((getVariant() !== 'hotel' &&
            data.plugin &&
            'hasShowFormOnline' in data.plugin &&
            data.plugin.hasShowFormOnline) ||
            getVariant() === 'event') && (
            <Suspense>
              <SpotSanityDates store={store} />
            </Suspense>
          )}
        {data.plugin &&
          'hasShowFormOnline' in data.plugin &&
          data.plugin.hasShowFormOnline}

        {hasActiveFacet(data?.content?.facets) && (
          <div
            className={cn(
              'relative flex min-h-full grow-0 items-center justify-self-start',
              isFiltersActive && '[&_.dot]:opacity-100'
            )}
          >
            <Dialog.Root>
              <Dialog.Trigger asChild>
                <Button
                  icon="material-symbols:instant-mix"
                  iconPosition="left"
                  className="h-full whitespace-nowrap"
                >
                  {`${D['fe.events.filters.title' as any]}`}
                  {filtersLength > 0 && ` (${filtersLength.toString()})`}
                </Button>
              </Dialog.Trigger>
              <Dialog.Content
                className="mb-lg gap-0 border-0 p-0 sm:max-w-[752px]"
                data-lenis-prevent
                showCloseButton={false}
              >
                <Dialog.Header />
                <div className="px-global max-h-[calc(100vh-var(--spacing-global)-(160px+3rem))] overflow-y-auto">
                  <SpotSanityFiltersModal data={data} store={store} />
                </div>
                <Dialog.Footer className="px-lg py-md flex bg-neutral-200 sm:justify-between">
                  <Button variant="tertiary" onClick={reset}>
                    {D['fe.events.filters.deleteAll']}
                  </Button>
                  <Dialog.Close asChild>
                    <Button type="submit" variant="primary">
                      {D['fe.events.filters.apply']} (
                      {storeFilteredItems.toString()})
                    </Button>
                  </Dialog.Close>
                </Dialog.Footer>
              </Dialog.Content>
            </Dialog.Root>
            <div className="dot ease-out-cubic absolute top-2 right-2 h-2 w-2 bg-neutral-600 opacity-0 transition-opacity duration-(--duration-default)" />
          </div>
        )}
      </div>
      <div className="py-md relative z-5 col-span-12 grid w-full grid-cols-2 items-center">
        <div>
          {storeItemsToShow ? (
            <p className="textstyle-sm-regular">{`1 - ${storeFilteredItems > storeItemsToShow ? storeItemsToShow : storeFilteredItems} ${D['fe.events.filters.resultsFrom']} ${storeFilteredItems}`}</p>
          ) : (
            <div className="gap-md flex items-center">
              <Button
                variant="text"
                as="button"
                nextLink={false}
                onClick={reset}
              >
                {data?.plugin?.noResultText || D['fe.events.filters.noResults']}
              </Button>
            </div>
          )}
        </div>
        <div className="gap-md flex items-center justify-self-end">
          {isDesktop &&
            data.type === 'spotsanity_eventlist' &&
            data?.plugin?.hasShowPdf && (
              <Suspense>
                <Pdf data={data} store={store} />
              </Suspense>
            )}
          {isDesktop &&
            filterUrlForBackend &&
            data.plugin &&
            'hasShowPdf' in data.plugin &&
            data.plugin.hasShowPdf &&
            'linkToPdf' in data.plugin &&
            data.plugin.linkToPdf && (
              <Button
                href={filterUrlForBackend}
                nextLink={false}
                variant="text"
                size="s"
              >
                {D['fe.events.generatePdf']}
              </Button>
            )}
          <SpotSanitySorts data={data} store={store} />
        </div>
      </div>
    </>
  )
}

export default memo(SpotSanityFilters)
